import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { playerAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FaComments, FaCalendarAlt, FaMapMarkerAlt, FaTimes, FaTrophy } from 'react-icons/fa';
import MatchScoreModal from '../components/MatchScoreModal';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB', lightBg: '#f8fafc' };

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
`;
const BackLink = styled(Link)`
  font-size: 0.875rem;
  color: ${BRAND.blue};
  text-decoration: none;
  margin-bottom: 1rem;
  display: inline-block;
  &:hover { text-decoration: underline; }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  border: 1px solid rgba(4,54,99,0.08);
`;
const Vs = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.75rem;
`;
const Meta = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  margin: 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const Score = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
  margin: 1rem 0;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
`;
const Btn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;
const BtnPrimary = styled(Btn)`
  background: ${BRAND.blue};
  color: #fff;
`;
const BtnDanger = styled(Btn)`
  background: #fef2f2;
  color: #b91c1c;
`;
const BtnScore = styled(Btn)`
  background: #10b981;
  color: #fff;
`;

// Convert MatchScoreModal payload (team1_set1, team2_set1, ...) to API format (side1Set1Games, side2Set1Games, side1Sets, side2Sets)
function modalScoresToApi(scores) {
  const t1s1 = parseInt(scores.team1_set1, 10) || 0;
  const t1s2 = parseInt(scores.team1_set2, 10) || 0;
  const t1s3 = scores.team1_set3 !== '' && scores.team1_set3 != null ? parseInt(scores.team1_set3, 10) : null;
  const t2s1 = parseInt(scores.team2_set1, 10) || 0;
  const t2s2 = parseInt(scores.team2_set2, 10) || 0;
  const t2s3 = scores.team2_set3 !== '' && scores.team2_set3 != null ? parseInt(scores.team2_set3, 10) : null;
  let side1Sets = 0;
  let side2Sets = 0;
  if (t1s1 > t2s1) side1Sets++; else if (t2s1 > t1s1) side2Sets++;
  if (t1s2 > t2s2) side1Sets++; else if (t2s2 > t1s2) side2Sets++;
  if (t1s3 != null && t2s3 != null) {
    if (t1s3 > t2s3) side1Sets++; else if (t2s3 > t1s3) side2Sets++;
  }
  return {
    side1Sets,
    side2Sets,
    side1Set1Games: t1s1,
    side2Set1Games: t2s1,
    side1Set2Games: t1s2,
    side2Set2Games: t2s2,
    side1Set3Games: t1s3,
    side2Set3Games: t2s3
  };
}

// Build existingScores for modal from match (side1 = you, side2 = opponent)
function matchToExistingScores(match) {
  if (!match || match.status !== 'completed') return null;
  return {
    team1_set1: match.side1Set1Games ?? '',
    team1_set2: match.side1Set2Games ?? '',
    team1_set3: match.side1Set3Games ?? '',
    team2_set1: match.side2Set1Games ?? '',
    team2_set2: match.side2Set2Games ?? '',
    team2_set3: match.side2Set3Games ?? ''
  };
}

export default function PlayerMatchDetailPage() {
  const { t } = useTranslation();
  const { matchId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [scoreModalOpen, setScoreModalOpen] = useState(false);

  const { data: matchRes, isLoading } = useQuery(
    ['playerMatch', matchId],
    () => playerAPI.getMatch(matchId),
    { enabled: !!matchId }
  );
  const match = matchRes?.data;

  const submitScore = useMutation(
    (score) => playerAPI.submitMatchScore(matchId, score),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['playerMatch', matchId]);
        queryClient.invalidateQueries(['playerMatches']);
        toast.success(t('playerMatchDetailPage.toastScoreSubmitted'));
        setScoreModalOpen(false);
      },
      onError: (err) =>
        toast.error(err?.response?.data?.message || t('playerMatchDetailPage.toastScoreFailed'))
    }
  );
  const cancelMatch = useMutation(
    () => playerAPI.cancelMatch(matchId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['playerMatches']);
        toast.success(t('playerMatchDetailPage.toastCancelled'));
        navigate('/player/find-match');
      },
      onError: (err) =>
        toast.error(err?.response?.data?.message || t('playerMatchDetailPage.toastCancelFailed'))
    }
  );

  const handleSubmitScoreFromModal = (scores) => {
    submitScore.mutate(modalScoresToApi(scores));
  };

  if (isLoading || !match) {
    return (
      <>
        <BackLink to="/player/find-match">{t('playerMatchDetailPage.backToFindMatch')}</BackLink>
        <PageTitle>{t('playerMatchDetailPage.loadingMatchTitle')}</PageTitle>
        <p>{t('playerMatchDetailPage.loading')}</p>
      </>
    );
  }

  const isCompleted = match.status === 'completed';
  const isScheduled = match.status === 'scheduled';
  const isCancelled = match.status === 'cancelled';
  const opponentLabel = match.opponent?.name || t('playerMatchDetailPage.opponentFallback');
  const matchDateRange = match.scheduledAt
    ? new Date(match.scheduledAt).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null;

  return (
    <>
      <BackLink to="/player/find-match">{t('playerMatchDetailPage.backToFindMatch')}</BackLink>
      <PageTitle>{t('playerMatchDetailPage.matchTitle', { name: opponentLabel })}</PageTitle>

      <Card>
        <Vs>{t('playerMatchDetailPage.vsLine', { name: opponentLabel })}</Vs>
        {match.scheduledAt && (
          <Meta><FaCalendarAlt /> {new Date(match.scheduledAt).toLocaleString()}</Meta>
        )}
        {match.location && <Meta><FaMapMarkerAlt /> {match.location}</Meta>}
        {match.notes && <Meta>{match.notes}</Meta>}

        {isCompleted && (
          <Score>
            {t('playerMatchDetailPage.scoreLine', { s1: match.side1Sets, s2: match.side2Sets })}
          </Score>
        )}

        <Actions>
          {match.conversationId && (
            <BtnPrimary as={Link} to={`/player/messages/${match.conversationId}`}>
              <FaComments size={14} /> {t('playerMatchDetailPage.chat')}
            </BtnPrimary>
          )}
          {isScheduled && (
            <BtnDanger onClick={() => cancelMatch.mutate()} disabled={cancelMatch.isLoading}>
              <FaTimes size={14} /> {t('playerMatchDetailPage.cancelMatch')}
            </BtnDanger>
          )}
          <BtnScore type="button" onClick={() => setScoreModalOpen(true)}>
            <FaTrophy size={14} />{' '}
            {isCompleted ? t('playerMatchDetailPage.viewScore') : t('playerMatchDetailPage.submitScore')}
          </BtnScore>
        </Actions>
      </Card>

      <MatchScoreModal
        isOpen={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        matchId={matchId}
        opponentName={opponentLabel}
        matchDateRange={matchDateRange}
        onSubmitScore={handleSubmitScoreFromModal}
        isIndividualMatch
        modalState={isCompleted ? 'view' : 'submit'}
        existingScores={matchToExistingScores(match)}
      />

      {isCancelled && <p style={{ color: '#64748b' }}>{t('playerMatchDetailPage.cancelledNotice')}</p>}
    </>
  );
}
